/**
 * AppSelect / AppAsyncSelect
 * Wrappers padronizados sobre react-select com defaults do projeto:
 *   - isClearable (X para limpar seleção)
 *   - noOptionsMessage traduzido
 *   - loadingMessage traduzido
 *   - placeholder genérico traduzido
 *   - visual consistente com o resto do sistema
 *
 * Uso básico:
 *   import { AppSelect, AppAsyncSelect } from '../../common/_components/AppSelect';
 *
 *   <AppSelect options={AREAS} onChange={...} />
 *   <AppAsyncSelect loadOptions={carregarProgramas} onChange={...} />
 *
 * Para um placeholder específico, passe a CHAVE de tradução em `placeholderKey`:
 *   <AppSelect placeholderKey="patients:form.placeholder.selectProgram" />
 * Evite passar `placeholder` com texto fixo — só use quando o texto vier de dado
 * cadastrado pelo usuário, que não deve ser traduzido.
 */

import PropTypes from 'prop-types';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useTranslation } from 'react-i18next';

const propTypesComuns = {
    /** Texto pronto. Use só para conteúdo cadastrado pelo usuário, que não é traduzido. */
    placeholder: PropTypes.string,
    /** Chave de tradução do placeholder — caminho preferido. */
    placeholderKey: PropTypes.string,
};

function useDefaultProps() {
    const { t } = useTranslation('common');

    return {
        isClearable: true,
        noOptionsMessage: () => t('select.noOptions'),
        loadingMessage: () => t('select.loading'),
        classNamePrefix: 'app-select',
    };
}

export function AppSelect({ placeholder, placeholderKey, ...props }) {
    const { t } = useTranslation('common');
    const defaultProps = useDefaultProps();

    return (
        <Select
            {...defaultProps}
            placeholder={placeholder ?? t(placeholderKey || 'common:select.placeholder')}
            {...props}
        />
    );
}

export function AppAsyncSelect({ placeholder, placeholderKey, ...props }) {
    const { t } = useTranslation('common');
    const defaultProps = useDefaultProps();

    return (
        <AsyncSelect
            {...defaultProps}
            cacheOptions
            defaultOptions
            placeholder={placeholder ?? t(placeholderKey || 'common:select.typeToSearch')}
            {...props}
        />
    );
}

AppSelect.propTypes = propTypesComuns;
AppAsyncSelect.propTypes = propTypesComuns;

export default AppSelect;
